// English strings: the reference dictionary. fr.ts must have exactly the same keys (checked by its type).
// Counts use i18next plurals (key_one / key_other); {{name}} is interpolated.

export const en = {
  common: {
    loading: 'Loading…',
    cancel: 'Cancel',
    continue: 'Continue',
    save: 'Save',
    saved: 'Saved.',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    more: 'Show more',
    yes: 'Yes',
    no: 'No',
    timezone: 'Time zone',
  },
  errors: {
    unavailable: 'The service is unavailable at the moment. Try again in a few minutes.',
    rateLimited: 'Too many attempts. Wait a minute and try again.',
    network: 'The connection failed. Check your network and try again.',
    notFound: 'This page does not exist.',
    home: 'Back to the dashboard',
    crashed: 'Something went wrong on this page.',
    newVersion: 'A new version of the dashboard is available.',
    reload: 'Reload',
  },
  reauth: {
    title: 'Confirm it is you',
    why: 'For this change, enter your password again.',
    wrong: 'That is not right. Try again.',
    confirm: 'Confirm',
    useCode: 'Use an authenticator code instead',
    usePassword: 'Use my password instead',
  },
  auth: {
    footer: 'Temperature monitoring for food businesses — HACCP records kept for you.',
    signInTitle: 'Sign in',
    signIn: 'Sign in',
    email: 'Email address',
    password: 'Password',
    forgot: 'Forgot your password?',
    noAccount: 'No account yet?',
    signUp: 'Create an account',
    contactUs: 'Contact us',
    closedTitle: 'Accounts are created by our team',
    closedBody: 'We set up each client’s organisation with them: its sites, equipment and sensors. Tell us about your needs and we will get back to you.',
    closedInvited: 'Already a client? Your account comes from the invitation your organisation or our team sent you by email.',
    haveAccountSignIn: 'I already have an account',
    useLink: 'Email me a sign-in link instead',
    usePassword: 'Sign in with my password',
    sendLink: 'Send the link',
    checkInbox: 'Check your inbox',
    linkSent: 'If an account exists for {{email}}, we have sent it a link. It is valid for 15 minutes.',
    backToSignIn: 'Back to sign-in',
    invalidCredentials: 'Wrong email address or password.',
    breached: 'This password appears in a known data breach. Choose another one.',
    invalidLink: 'This link is invalid or has expired',
    invalidCode: 'Wrong code. Check your device’s clock and try again.',
    challengeExpired: 'The sign-in took too long. Start again.',
    twoFactorTitle: 'Two-step verification',
    totpHint: 'Enter the 6-digit code from your authenticator app.',
    recoveryHint: 'Enter one of the recovery codes you saved when you set up two-step verification.',
    totpCode: 'Authenticator code',
    recoveryCode: 'Recovery code',
    useRecovery: 'Use a recovery code',
    useTotp: 'Use my authenticator app',
    verify: 'Verify',
    signUpTitle: 'Create your account',
    signInToContinue: 'Create an account or sign in to continue.',
    name: 'Your name',
    passwordHint: 'At least 12 characters. A short sentence works well.',
    createAccount: 'Create my account',
    haveAccount: 'Already have an account?',
    verifySent: 'We have sent a confirmation link to {{email}}. Open it to activate your account.',
    verifying: 'Confirming your address…',
    verifyFailed: 'Sign in and ask for a new confirmation email, or create your account again.',
    emailVerified: 'Your address is confirmed',
    signingIn: 'Signing you in…',
    openingSupport: 'Opening the support session…',
    supportLinkFailed: 'A support link works once, within 5 minutes, and only while the organisation allows support access. Create a new one from the back-office.',
    supportEnded: 'The support session has ended',
    supportEndedBody: 'You can close this window.',
    linkFailed: 'Sign-in links work once and for 15 minutes. Ask for a new one.',
    forgotTitle: 'Reset your password',
    forgotHint: 'Enter your email address: we will send you a link to choose a new password.',
    sendResetLink: 'Send the link',
    resetSent: 'If an account exists for {{email}}, we have sent it a link. It is valid for 1 hour.',
    resetTitle: 'Choose a new password',
    newPassword: 'New password',
    setPassword: 'Save the password',
    passwordChanged: 'Your password has been changed',
    passwordChangedHint: 'Your other sessions have been signed out. You can now sign in with the new password.',
  },
  invite: {
    title: 'Join an organisation',
    signInFirst: 'Sign in, or create an account with the address the invitation was sent to.',
    joining: 'Joining the organisation…',
    missing: 'The invitation link is incomplete. Open it again from the email.',
    failed: 'The invitation could not be accepted',
    failedBody: 'It may have expired or been revoked, or it was sent to another address than {{email}}. Ask for a new one.',
    invalidTitle: 'This invitation is no longer valid',
    invalidBody: 'It has expired, been used or been replaced. Ask your contact for a new one: the person who invited you, or our team.',
    joinTitle: 'Join {{org}}',
    forRole: 'You are invited as {{role}}.',
    forAddress: 'The invitation was sent to {{email}}: your account uses this address.',
    language: 'Language',
    createAndJoin: 'Create my account and join',
    accountExists: 'You already have an account',
    accountExistsBody: 'An account exists for {{email}}. Sign in with it: the invitation will be accepted right after.',
  },
  shell: {
    organisation: 'Organisation',
    navigation: 'Main navigation',
    profile: 'My profile',
    newOrg: 'New organisation',
    signOut: 'Sign out',
    themeDark: 'Switch to the dark theme',
    themeLight: 'Switch to the light theme',
    supportReadOnly: 'Support session as {{name}}, read-only, until {{time}}. Every access is recorded.',
    supportWrite: 'Support session as {{name}}, changes allowed, until {{time}}. Every action is recorded.',
    endSupport: 'End the session',
    verifyEmail: 'Confirm your address: we have sent a link to {{email}}.',
    twoFactorRequired: 'Two-step verification required',
    twoFactorRequiredBody: '{{org}} requires two-step verification for your role. Turn it on to continue.',
    enableTwoFactor: 'Turn it on',
    restricted: 'Read-only account',
    restrictedOwner: 'The subscription is not paid: changes are blocked, and alerts go out by email only.',
    restrictedMember: 'The subscription is not paid: changes are blocked, and alerts go out by email only. Contact the account owner.',
    pastDue: 'The last payment failed. Update the payment method to keep full access.',
    pastDueRevolut: 'The last renewal failed. Pay it to keep full access.',
    trialEnds: 'Your free trial ends on {{date}}.',
    canceled: 'Subscription ended',
    canceledOwner: 'Changes are blocked, and monitoring stops on {{date}}. Subscribe again to keep your data.',
    canceledMember: 'Changes are blocked, and monitoring will stop. Contact the account owner.',
    billingAction: 'Subscription',
    erasurePending: 'This organisation and all its data will be erased on {{date}}.',
    erasureCancel: 'Cancel the erasure',
  },
  nav: {
    overview: 'Overview',
    alarms: 'Alarms',
    devices: 'Devices',
    settings: 'Settings',
    maintenance: 'Maintenance',
    energy: 'Energy',
    haccp: 'HACCP',
    lora: 'LoRa',
    exports: 'Exports',
  },
  tour: {
    start: 'Guided tour',
    setup: 'Setup assistant',
    label: 'Guided tour of the interface',
    step: 'Step {{n}} / {{count}}',
    previous: 'Previous',
    next: 'Next',
    finish: 'Finish',
    menuAdmin: 'Your profile and sign-out. Start the guided tour again here whenever you like, and the setup assistant to add sites, equipment and devices.',
    steps: {
      welcome: { title: 'Welcome to Maillon Froid', body: 'In one minute, here is where to find the essentials. Arrows or Enter to move on, Escape to leave.' },
      org: { title: 'Your organisation', body: 'The organisation shown. If you follow several, switch here.' },
      navOverview: { title: 'Overview', body: 'All your equipment by site, with live temperatures. This is the home page.' },
      navAlarms: { title: 'Alarms', body: 'Open alarms and their history. The red number counts the open ones; acknowledge them here, with the corrective action.' },
      navDevices: { title: 'Devices', body: 'Devices and their probes: state, battery, signal. “Add a device” installs a new one.' },
      navLora: { title: 'LoRa', body: 'Your LoRa bases, which relay the radio sensors.' },
      navHaccp: { title: 'HACCP', body: 'The HACCP plan’s records (cooling, receptions, cleaning), also entered on a phone.' },
      navMaintenance: { title: 'Maintenance', body: 'Work on your equipment, its costs and the upkeep to plan.' },
      navEnergy: { title: 'Energy', body: 'Your equipment’s electricity use and what it costs.' },
      navSettings: { title: 'Settings', body: 'Sites, equipment, alarm thresholds, team and notifications. PDF and CSV exports for an inspection are in Settings → Exports.' },
      navExports: { title: 'Exports', body: 'PDF and CSV reports of your temperatures and records, for a health inspection.' },
      live: { title: 'Live', body: 'Green: values arrive live. Orange: reconnecting. Grey: offline, the page is no longer up to date.' },
      alarmBanner: { title: 'Open alarms', body: 'This banner shows as soon as an alarm is open. “See the alarms” opens the list.' },
      views: { title: 'Three views', body: 'Tiles, charts of all equipment, or a map of sites and trucks.' },
      asset: { title: 'A piece of equipment', body: 'The border and badge give its state: green OK, orange warning, red alarm, purple offline, grey no data. Tap the tile to open its chart and history.' },
      menu: { title: 'Your menu', body: 'Your profile and sign-out. Start the guided tour again here whenever you like.' },
      done: { title: 'All set', body: 'You know where everything is. The guided tour stays in your menu, top right.' },
    },
  },
  live: {
    connecting: 'Connecting…',
    live: 'Live',
    offline: 'Not live — refreshed every minute',
  },
  onboarding: {
    title: 'Create your organisation',
    subtitle: 'Your company or establishment: its sites, equipment and team are managed together.',
    name: 'Name',
    nameHint: 'As your team knows it, e.g. “Boulangerie Martin”.',
    plan: 'Plan',
    trial: 'Free for {{days}} days, no card needed. You can change plan at any time.',
    planLimits: 'up to {{devices}} devices and {{users}} users',
    create: 'Create the organisation',
    verifyFirst: 'Confirm your email address first: open the link we sent you.',
    closedTitle: 'Your organisation is on its way',
    closedBody: 'Our team sets up each client’s organisation, then invites its members. You do not need to create one.',
    closedInvitation: 'As soon as you are invited, open the link in the email sent to {{email}}: you will land in your organisation.',
    closedContact: 'A question? Contact us',
  },
  overview: {
    title: 'Overview',
    live: 'Updated live',
    fromDatabase: 'Last known values',
    activeAlarms_one: '{{count}} alarm in progress',
    activeAlarms_other: '{{count}} alarms in progress',
    awaiting_one: '{{count}} alarm to acknowledge',
    awaiting_other: '{{count}} alarms to acknowledge',
    alarmsHint: 'Acknowledge each alarm and record what you did: it goes into the HACCP record.',
    seeAlarms: 'See the alarms',
    noSites: 'No site yet',
    noSitesHint: 'Start with a site (a shop, a kitchen, a warehouse), then add the fridges and freezers in it.',
    addSite: 'Add a site',
    noAssets: 'No equipment yet',
    noAssetsHint: 'Add a fridge, freezer or cold room, then connect a device to it.',
    addAsset: 'Add equipment',
    siteEmpty: 'No equipment on this site.',
    allSites: 'All sites',
    site: 'Site',
    doorOpen: 'Door open for {{duration}}',
    toAcknowledge_one: '{{count}} to acknowledge',
    toAcknowledge_other: '{{count}} to acknowledge',
    seen: 'Seen {{ago}}',
    neverSeen: 'No reading yet',
    position: 'Position {{ago}}',
    view: 'View',
    viewCards: 'Tiles',
    viewCharts: 'Charts',
    viewMap: 'Map',
  },
  map: {
    region: 'Map of the sites and vehicles',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    attribution: 'Map credits',
    unavailable: 'The map cannot be shown here. The list has every site and vehicle.',
    loading: 'Loading the map…',
    site: 'Site',
    vehicle: 'Vehicle',
    legend: 'Legend',
    statusFilter: 'Status',
    allStatuses: 'All statuses',
    problems: 'Problems only',
    list: 'On the map',
    nothing: 'Nothing on the map with this filter.',
    noPoints: 'No site is on the map yet',
    noPointsHint: 'Give your sites a location in Settings → Sites and equipment: click on a small map, or type the coordinates.',
    missing: 'Sites without a location',
    missingHint: 'They are not on the map. An Owner or Admin can place them in Settings → Sites and equipment.',
    place: 'Place the sites',
    choose: 'Choose a site or a vehicle, on the map or in the list, to see its equipment.',
    close: 'Close',
    equipment: 'Equipment',
    noEquipment: 'No equipment here.',
    alarms: 'Alarms in progress',
    vehicleOf: 'Vehicle of the site {{site}}',
    since: 'since {{ago}}',
  },
  status: {
    alarm: 'Alarm',
    warning: 'Warning',
    offline: 'Offline',
    ok: 'OK',
    no_data: 'No data',
  },
  assetType: {
    fridge: 'Fridge',
    freezer: 'Freezer',
    cold_room: 'Cold room',
    blast_freezer: 'Blast freezer',
    truck: 'Refrigerated vehicle',
    other: 'Other…',
  },
  customType: {
    label: 'Type name',
    labelHint: 'e.g. “Wine cellar”, “Display case”, “Outdoors”.',
    min: 'Minimum °C (optional)',
    max: 'Maximum °C (optional)',
  },
  severity: {
    warning: 'Warning',
    critical: 'Critical',
  },
  alarmState: {
    active: 'In progress',
    awaitingAck: 'Ended, to acknowledge',
    acknowledged: 'Acknowledged',
    resolved: 'Resolved',
  },
  role: {
    owner: 'Owner',
    admin: 'Administrator',
    operator: 'Operator',
    viewer: 'Viewer',
  },
  roleHint: {
    admin: 'Manages sites, equipment, devices, alert rules and the team.',
    operator: 'Acknowledges alarms, records corrective actions and notes, mutes alerts for a few hours.',
    viewer: 'Sees temperatures, alarms and records, and downloads exports.',
  },
  alarmType: {
    high: 'Temperature too high',
    low: 'Temperature too low',
    rate_of_change: 'Sudden temperature change',
    offline: 'Device offline',
    door_open: 'Door left open',
    low_battery: 'Low battery',
    probe_fault: 'Probe fault',
    power_loss: 'Power cut',
    no_consumption: 'No consumption',
    compressor_continuous: 'Compressor running non-stop',
    short_cycling: 'Compressor short-cycling',
    meter_offline: 'Meter offline',
    cooling_late: 'Cooling too slow',
  },
  alarms: {
    title: 'Alarms',
    show: 'Show',
    open: 'In progress or to acknowledge',
    closed: 'Closed',
    severity: 'Severity',
    allSeverities: 'All severities',
    none: 'No alarm in this period.',
    noneOpen: 'No alarm in progress. All is well.',
    alarm: 'Alarm',
    asset: 'Equipment',
    since: 'Since',
    value: 'Value',
    state: 'State',
    retrospective: 'recorded offline',
    deviceLevel: 'Device',
    lasted: 'lasted {{duration}}',
    notFound: 'This alarm does not exist or is not in this organisation.',
    notFoundBody: 'It may belong to an organisation you are no longer a member of.',
    retrospectiveHint: 'The device was offline when this happened: the alarm was raised when its readings arrived.',
    summary: 'Summary',
    conditionSince: 'Condition since',
    triggeredAt: 'Alarm raised',
    clearedAt: 'Back to normal',
    triggerValue: 'Value at the alarm',
    peak: 'Worst value',
    level: 'Escalation level',
    acknowledgedBy: 'Acknowledged by',
    rule: 'Rule',
    actions: 'Corrective actions',
    noActions: 'No corrective action recorded yet.',
    disposition: 'Products',
    dispositionValue: 'Products: {{value}}',
    photo: 'Photo',
    timeline: 'Timeline',
    notifications: 'Notifications sent',
    noNotifications: 'No notification sent.',
    recipient: 'Recipient',
    channel: 'Channel',
    sent: 'Sent',
    delivery: 'Delivery',
    levelN: 'level {{n}}',
    alreadyAcknowledged: 'Someone has just acknowledged this alarm. The page has been refreshed.',
    category: 'What was done',
    dispositionHint: 'What became of the products, e.g. “kept, below 8 °C for less than 2 h” or “3 kg discarded”.',
    comment: 'Comment',
    photoOptional: 'Photo (optional)',
    photoHint: 'JPEG, PNG or WebP, up to 10 MB.',
    acknowledge: 'Acknowledge',
    acknowledgeHint: 'Say what you did about it: the alarm and the action go into the HACCP record.',
    addAction: 'Add a corrective action',
  },
  alarmEvent: {
    triggered: 'Alarm raised',
    notified: 'Notifications sent',
    escalated: 'Escalated to the next level',
    reminder: 'Reminder sent',
    acknowledged: 'Acknowledged',
    comment: 'Comment',
    cleared: 'Back to normal',
    resolved: 'Resolved',
    reopened: 'Reopened',
    muted_excursion: 'Excursion during a muted period',
    maintenance: 'Maintenance intervention logged',
  },
  notificationReason: {
    trigger: 'Alarm',
    escalation: 'Escalation',
    reminder: 'Reminder',
    retrospective: 'Recorded offline',
    cleared: 'Back to normal',
    fallback: 'Fallback',
    advisory: 'Maintenance advisory',
  },
  notificationStatus: {
    queued: 'Queued',
    accepted: 'Sent',
    delivered: 'Delivered',
    read: 'Read',
    failed: 'Failed',
    quota_exceeded: 'Quota reached',
    suppressed_quiet_hours: 'Quiet hours',
  },
  channel: {
    email: 'Email',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
  },
  correctiveCategory: {
    door_closed: 'Door closed',
    moved_products: 'Products moved',
    product_discarded: 'Products discarded',
    technician_called: 'Technician called',
    probe_displaced: 'Probe put back in place',
    false_alarm: 'False alarm',
    other: 'Other',
  },
  assets: {
    notFound: 'This equipment does not exist or has been archived.',
    rules: 'Alert rules',
    csv: 'Download CSV',
    openAlarms: 'Alarms in progress',
    temperatures: 'Temperatures',
    range: 'Period',
    noProbes: 'No probe on this equipment',
    noProbesHint: 'Assign a device probe to it from the Devices page.',
    noData: 'No reading in this period.',
    rollupHint: 'Averages per {{tier}}, with the minimum and maximum shaded.',
    doorSummary_one: 'Door opened once, for {{total}} in all.',
    doorSummary_other: 'Door opened {{count}} times, for {{total}} in all.',
    statistics: 'Statistics for the period',
    probe: 'Probe',
    probes: 'Probes',
    value: 'Last value',
    calibration: 'Calibration',
    device: 'Device',
    door: 'door',
    open: 'Open',
    thresholdHigh: 'High threshold {{value}}',
    thresholdLow: 'Low threshold {{value}}',
  },
  ranges: {
    '24h': '24 h',
    '7d': '7 days',
    '30d': '30 days',
    '90d': '90 days',
    '1y': '1 year',
  },
  tiers: {
    auto: 'automatic',
    raw: 'reading',
    '5m': '5 minutes',
    '1h': 'hour',
    local_day: 'day',
  },
  stats: {
    min: 'Min',
    max: 'Max',
    avg: 'Average',
    mkt: 'MKT',
    completeness: 'Completeness',
  },
  annotations: {
    title: 'Notes',
    add: 'Add a note',
    hint: 'Explain a reading for the record: a defrost, a delivery, a cleaning, a faulty value.',
    category: 'Kind',
    from: 'From',
    to: 'To',
    body: 'Note',
    none: 'No note in this period.',
    superseded: 'superseded',
  },
  annotationCategory: {
    correction: 'Correction',
    defrost: 'Defrost',
    maintenance: 'Maintenance',
    cleaning: 'Cleaning',
    delivery: 'Delivery',
    note: 'Note',
  },
  devices: {
    title: 'Devices',
    claim: 'Add a device',
    none: 'No device yet',
    noneHint: 'Each device carries up to four probes. Add it with the code printed on its label.',
    device: 'Device',
    site: 'Site',
    state: 'State',
    lastSeen: 'Last seen',
    battery: 'Battery',
    signal: 'Signal',
    firmware: 'Firmware',
    mains: 'Mains',
    online: 'Online',
    offline: 'Offline',
    pending: 'Waiting for its first connection',
    revoked: 'Revoked',
    released: 'Released',
    notFound: 'This device does not exist or is no longer in this organisation.',
    release: 'Release',
    revoke: 'Revoke',
    waiting: 'Waiting for the device',
    waitingHint: 'Plug it in near a Wi-Fi or mobile network: it connects by itself within a few minutes.',
    health: 'Health',
    power: 'Power',
    onBattery: 'On battery',
    lastReset: 'Last restart',
    claimedAt: 'Added on',
    sampling: 'Measurements',
    samplingValue: 'every {{sample}} s, sent every {{upload}} s',
    configVersion: 'Configuration version',
    probes: 'Probes',
    noProbes: 'The device has not reported its probes yet.',
    settings: 'Device settings',
    label: 'Name',
    sampleEvery: 'Measure every',
    uploadEvery: 'Send every',
    seconds: 'seconds',
    settingsHint: 'The device picks up the new settings at its next connection.',
    revokeTitle: 'Revoke this device?',
    releaseTitle: 'Release this device?',
    revokeHint: 'Its credentials stop working at once, for good: use this if it has been lost or stolen. Its readings are kept.',
    releaseHint: 'It leaves this organisation and can be added to another one. Its readings stay here.',
    probeLimit: 'Your plan’s probe limit is reached. Change plan to add more.',
    probeLabel: 'Name of this probe',
    probe: 'Probe',
    lastValue: 'Last value',
    asset: 'Equipment',
    unassigned: 'Not assigned',
    rename: 'Rename',
    calibrate: 'Calibrate',
    calibrationTitle: 'Calibration — {{probe}}',
    calibrationHint: 'Compare the probe with a reference thermometer and enter the correction to apply to its readings.',
    offset: 'Correction',
    certificateValidUntil: 'Certificate valid until',
    certificate: 'Certificate',
    certificateHint: 'PDF or image of the calibration certificate, up to 10 MB.',
    note: 'Note',
    calibrationHistory: 'History',
    feeds: 'Feed',
    feedsTitle: 'Live feed — {{probe}}',
    feedsHint: 'A secret link to this probe’s readings, for tools that cannot sign in: Home Assistant, Grafana, a spreadsheet. Anyone with the link can read the probe’s temperatures (nothing else, and change nothing) until you revoke it.',
    feedLabel: 'What it is for',
    feedLabelHint: 'e.g. Home Assistant',
    feedCreate: 'Create a link',
    feedOnce: 'Copy this link now',
    feedOnceHint: 'It is shown only once. If it is lost, revoke it and create a new one.',
    feedFormats: 'Add ?format=csv for CSV or ?format=prometheus for Prometheus, and &hours=48 for up to 168 hours (24 by default). Up to 30 requests a minute.',
    feedActive: 'Active links',
    feedNone: 'No link yet.',
    feedCreated: 'Created {{when}}',
    feedUsed: 'last read {{when}}',
    feedNeverUsed: 'never read',
    feedCopy: 'Copy',
    feedCopied: 'Copied',
    feedRevoke: 'Revoke',
    feedRevokeConfirm: 'Revoke this link? Tools that use it stop receiving the probe’s readings at once.',
    feedLimit: 'This probe already has 10 links: revoke one first.',
    from: 'Since',
    view: 'View',
    live: {
      on: 'Live',
      starting: 'Live mode',
      every: 'every {{s}} s',
      left: '{{time}} left',
      waiting: 'Waiting for the device…',
      waitingUntil: 'Waiting for the device… up to {{s}} s',
      paused: 'Live paused',
      resume: 'Resume',
      noValue: 'No value yet',
      curve: 'Last 10 minutes: {{min}} to {{max}}',
    },
  },
  probeKind: {
    temperature: 'Temperature',
    door: 'Door',
    energy: 'Power meter',
  },
  setup: {
    title: 'Set up {{org}}',
    subtitle: 'Six steps, about 15 minutes. Each can be finished later from Settings.',
    later: 'Finish later',
    steps: 'Setup steps',
    step: { sites: 'Sites', equipment: 'Equipment', devices: 'Devices', probes: 'Probes', alarms: 'Alerts', team: 'Team' },
    progress: '{{done}} of {{total}} steps done',
    next: 'Next: {{step}}',
    finish: 'Finish',
    needSite: 'Add a site first (step 1).',
    needEquipment: 'Add your equipment first (step 2).',
    resumeTitle: 'Your setup is not finished',
    resume: 'Resume setup',
    resumeHint: 'No probe measures any equipment yet: the setup assistant takes you through the devices and their probes.',
    sites: {
      intro: 'A site is a place with its own address and time zone: a restaurant, a kitchen, a warehouse. Daily records and reports follow its time zone.',
      name: 'Site name', nameHint: 'e.g. “Restaurant, rue du Marché”.', address: 'Address (optional)', timezone: 'Time zone',
      add: 'Add the site', addAnother: 'Add another site',
    },
    equipment: {
      intro: 'Each fridge, freezer or cold room you want to monitor. Its type sets the default alerts, which you can change in the Alerts step.',
      name: 'Name', nameHint: 'As your team calls it, e.g. “Fridge 1”.', type: 'Type', volume: 'Volume in litres (optional)',
      template: 'Default alerts:', add: 'Add the equipment', addAnother: 'Add another',
    },
    devices: {
      intro: 'The devices measure and send the temperatures. Each has an ID (CCK-…) and a claim code on its label.',
      kind: { wifi: 'Wi-Fi box', loraBase: 'LoRa base', loraNode: 'LoRa sensor' },
      wifi: {
        s1: 'Connect its probes, then power it with a USB-C adapter.',
        s2: 'With your phone, join the Wi-Fi network named after its ID (password: the first 8 characters of its code, without the dash), open http://192.168.4.1/ and enter your Wi-Fi.',
        s3: 'Enter its ID and code below.',
        labelHint: 'Where it is, e.g. “Kitchen”.',
      },
      loraBase: {
        s1: 'Place it within reach of your Wi-Fi, as high as possible, antenna upright, and power it.',
        s2: 'Connect it to Wi-Fi like a Wi-Fi box: its network carries its ID, the password is the first 8 characters of its code.',
        s3: 'Enter its ID and code below. It then relays the LoRa sensors it hears: see the LoRa page.',
        labelHint: 'e.g. “Base, kitchen”.',
      },
      loraNode: {
        s1: 'For a fridge or room out of Wi-Fi reach: it sends its readings by radio to a LoRa base.',
        s2: 'Claim a LoRa base first, then power the sensor within range (a few hundred metres, through walls).',
        s3: 'Enter its ID and code below: it joins through the base within a minute or two.',
        labelHint: 'Where it is, e.g. “Cellar”.',
      },
      claim: 'Claim the device',
      waiting: 'Waiting for the devices’ first contact… (refreshed every 10 s)',
    },
    probes: {
      intro: 'Each probe and each power meter of your devices: say which equipment it measures. New probes appear here within a few minutes of their device’s first contact.',
      none: 'No probe yet: they appear once a device has sent its first readings.',
      labelPlaceholder: 'Name (optional), e.g. “Top shelf”',
      warmHint: 'Hold a probe in your hand: its value rises, so you know which is which.',
    },
    alarms: {
      intro: 'The alerts applied to each piece of equipment. The defaults suit most cases; adjust them to your HACCP plan if needed.',
      adjust: 'Adjust', none: 'No alert on this equipment: add at least one.',
    },
    team: {
      intro: 'Who receives the alerts: invite your team. Operators acknowledge alarms; administrators also manage the setup.',
      invited: 'Invited', email: 'Email address', role: 'Role', invite: 'Invite',
      notifications: 'Alerts go by email; add an SMS, WhatsApp or Telegram number for critical ones:',
      notificationsLink: 'My notifications',
    },
  },
  lora: {
    title: 'LoRa bases',
    subtitle: 'The bases relay your LoRa sensors’ readings over Wi-Fi: those out of Wi-Fi reach, in a cellar or a cold store.',
    add: 'Add a LoRa base',
    none: 'No LoRa base yet',
    noneHint: 'A LoRa base sits within Wi-Fi reach and picks up, by radio, the LoRa sensors placed up to a few hundred metres away, through walls.',
    nodes: 'Sensors linked',
    working: 'Working',
    weak: 'Weak signal',
    relayed: 'Other sensors relayed',
    relayedHint: 'Other organisations’ LoRa sensors your base relays: their readings stay encrypted and invisible to you.',
    lastSeen: 'Last contact {{when}}',
    neverSeen: 'Never connected yet',
    otherOrgs_one: 'also relays {{count}} sensor of another organisation',
    otherOrgs_other: 'also relays {{count}} sensors of other organisations',
    node: 'Sensor',
    signalCol: 'Signal',
    heard: 'Last heard',
    inService: 'Working',
    signal: { good: 'Good', fair: 'Fair', weak: 'Weak' },
    signalLegend: 'Signal at SF9: weak below −7 dB of SNR or −118 dBm (frames may be lost), fair below 0 dB or −105 dBm. The base refreshes each sensor’s signal at least every 10 minutes while it hears it.',
    weakTitle: 'Some sensors are heard poorly',
    weakHint: 'Their readings may arrive late or be resent. Raise the base, keep its antenna upright and away from metal, or move it closer to those sensors.',
    nodesTitle: 'LoRa sensors heard by this base',
    noNodes: 'This base has not heard any of your LoRa sensors yet. Power one on within range: it appears here within a few minutes once claimed.',
    notFound: 'This device is not a LoRa base of this organisation.',
    deviceSettings: 'Device settings',
    onboarding: {
      title: 'Add a LoRa base',
      subtitle: 'About 5 minutes. Have the base, its USB-C power supply and its label (ID and code) at hand.',
      step1: '1. Place the base',
      place1: 'Within reach of your Wi-Fi (2.4 GHz), plugged into a 5 V USB-C power supply that stays on.',
      place2: 'As high as possible, antenna upright, away from metal (shelves, cold room walls).',
      place3: 'Between the Wi-Fi and the LoRa sensors: a few hundred metres through a few walls is usually fine.',
      place4: 'One base can serve up to 16 LoRa sensors.',
      step2: '2. Connect it to Wi-Fi',
      wifi1: 'With your phone, join the Wi-Fi network named after the base’s ID (e.g. CCK-7Q2M9X4T). Its password: the first 8 characters of the code on the label, without the dash.',
      wifi2: 'Open http://192.168.4.1/, choose your Wi-Fi network and enter its password.',
      wifi3: 'The base connects: its light flashes every 2 seconds until it is claimed, then beats like a heart.',
      wifiHint: 'The setup network closes after 15 minutes; a short press on the base’s BOOT button opens it again.',
      step3: '3. Claim it',
      labelHint: 'Where it is, e.g. “Kitchen, above the door”.',
      claim: 'Claim the base',
      claimed: 'Base claimed',
      step4: '4. The base comes online',
      waiting: 'Waiting for the base’s first contact (within a few minutes once it is on Wi-Fi).',
      online: 'The base is online.',
      step5: '5. Its LoRa sensors',
      nodesHint: 'Power on your LoRa sensors and claim each with its own code (Devices → Add a device). They appear here, with their signal, as the base hears them.',
      listening: 'Listening… no sensor heard yet.',
      addNode: 'Claim a LoRa sensor',
    },
  },
  claim: {
    title: 'Add a device',
    subtitle: 'The code is on the label under the device and on its box.',
    hardwareId: 'Device ID',
    hardwareIdHint: 'E.g. CCK-7Q2M9X4T.',
    code: 'Activation code',
    codeHint: '12 characters, under the ID (dashes optional).',
    labelHint: 'Where it is, e.g. “Kitchen — fridge 2”.',
    submit: 'Add the device',
    wrongCode: 'This ID and code do not match. Check the label.',
    elsewhere: 'This device belongs to another organisation. It must be released there first.',
    limit: 'Your plan’s device limit is reached. Change plan to add more.',
    claimed: 'Device added',
    step2: 'Switch it on',
    online: 'The device is connected.',
    waiting: 'Waiting for the first connection…',
    power: 'Plug the device in, or insert its batteries.',
    network: 'Keep it within reach of the Wi-Fi or the mobile network.',
    wait: 'It connects within a few minutes; this page updates by itself.',
    step3: 'Assign its probes',
    noProbesYet: 'The probes appear here after the first connection.',
    done: 'Done',
  },
  kiosk: {
    label: 'Kiosk',
    leave: 'Leave',
    leaveConfirm: 'Leave kiosk mode on this screen? It will need the kiosk link again, or a sign-in.',
    readOnly: 'Read-only dashboard: data and downloads. Changes are made on app.maillonfroid.com.',
    opening: 'Opening the kiosk…',
    invalid: 'This kiosk link no longer works',
    invalidBody: 'It has been revoked, or it is incomplete. Ask an administrator of your organisation for a new link (Settings → Kiosks).',
  },
  kiosks: {
    subtitle: 'A kiosk link shows the dashboard on a wall screen or a tablet without signing in: data and downloads only, for the whole organisation or one site, until you revoke it.',
    new: 'New kiosk link',
    name: 'Name',
    nameHint: 'Where the screen is, e.g. Kitchen',
    scope: 'Shows',
    wholeOrg: 'The whole organisation',
    create: 'Create the link',
    once: 'Copy this link now',
    onceHint: 'It is shown only once. Open it on the screen: the screen stays signed in until you revoke the link. If it is lost, revoke it and create a new one.',
    copy: 'Copy',
    copied: 'Copied',
    active: 'Active kiosk links',
    none: 'No kiosk link yet.',
    created: 'Created {{when}}',
    used: 'last used {{when}}',
    neverUsed: 'never used',
    revoke: 'Revoke',
    revokeConfirm: 'Revoke this kiosk link? The screens that use it stop showing data at once.',
    limit: 'Your organisation already has 50 kiosk links: revoke one first.',
  },
  settings: {
    organisation: 'Organisation',
    members: 'Team',
    sites: 'Sites and equipment',
    rules: 'Alert rules',
    escalation: 'Escalation',
    notifications: 'My notifications',
    exports: 'Exports',
    audit: 'Audit log',
    billing: 'Subscription',
    haccp: 'HACCP',
    kiosks: 'Kiosks',
  },
  billing: {
    state: {
      trialing: 'Free trial',
      active: 'Active',
      past_due: 'Payment failed',
      restricted: 'Read-only',
      canceled: 'Ended',
    },
    price: '{{amount}} per {{interval}}, excl. VAT',
    interval: { month: 'month', year: 'year' },
    trialLocal: 'Your free trial ends on {{date}}. Choose a plan to keep monitoring without interruption.',
    trialCarried: 'The rest of your trial carries over: the first payment is on {{date}}.',
    trialStripe: 'Free until {{date}}, then paid automatically.',
    renews: 'Renews on {{date}}.',
    endsOn: 'Ends on {{date}}; it will not renew.',
    scheduledChange: 'A change of plan takes effect at the end of the period.',
    noPaymentMethod: 'No payment method on file: add one in the billing portal.',
    pastDue: 'The last payment failed. Update the payment method before {{date}}; after that, the account becomes read-only.',
    restricted: 'The account is read-only: changes are blocked, and alerts go out by email only. Without payment by {{date}}, the subscription is cancelled.',
    restrictedTrial: 'Your trial has ended and the account is read-only. Choose a plan to restore it; without a subscription by {{date}}, the account is closed.',
    canceled: 'The subscription ended on {{date}}.',
    ingestionUntil: 'Monitoring of your devices stops on {{date}}; until then, alerts go out by email only.',
    ingestionStopped: 'Monitoring of your devices stopped on {{date}}.',
    erasureAfter: 'Your data is kept until {{date}}, then erased. Export it first if you need it, or subscribe again to keep it.',
    manage: 'Invoices, payment and plan',
    manageHint: 'The billing portal (Stripe): invoices, payment method, VAT number, change of plan and cancellation.',
    choose: 'Choose a plan',
    monthly: 'Monthly',
    yearly: 'Yearly',
    yearlySaving: '−{{pct}}%',
    subscribe: 'Subscribe',
    current: 'Current plan',
    limitSites_one: '{{count}} site',
    limitSites_other: '{{count}} sites',
    limitDevices_one: '{{count}} device',
    limitDevices_other: '{{count}} devices',
    limitUsers_one: '{{count}} user',
    limitUsers_other: '{{count}} users',
    over: 'Your usage exceeds this plan: {{items}}.',
    overItem: '{{used}} {{what}} for {{max}}',
    changeInPortal: 'To change plan, open the billing portal: it offers the plans your usage fits.',
    returned: 'Payment received: your subscription is being set up…',
    paymentReturned: 'Payment received: your subscription is being updated…',
    unavailable: 'Online payment is not available here. Contact us to subscribe.',
    providerDown: 'The payment service is not responding. Try again in a moment.',
    priceVat: '{{amount}} per {{interval}}, VAT included',
    exclVat: 'excl. VAT',
    inclVat: 'VAT included',
    paidWith: 'Paid through {{provider}}.',
    provider: { stripe: 'Stripe', revolut: 'Revolut' },
    payWith: 'Payment',
    payWithHint: {
      stripe: 'By card, through Stripe. Prices exclude VAT, which is added at checkout; invoices are in the billing portal.',
      revolut: 'With Revolut Pay or a card, through Revolut. Prices include VAT; your payments are listed on this page.',
    },
    scheduledPlan: 'Changes to {{plan}} on {{date}}.',
    pastDueRevolut: 'The last renewal failed. Pay it before {{date}}; after that, the account becomes read-only.',
    revolut: 'Your subscription with Revolut',
    revolutHint: 'Revolut takes each renewal from the card you first paid with. A change of plan applies from the next renewal.',
    payNow: 'Pay the renewal',
    cancel: 'Cancel the subscription',
    cancelTitle: 'Cancel the subscription?',
    cancelBody: 'It ends on {{date}}, at the end of the period already paid; until then, everything works as now. Your data is then kept for 90 days.',
    cancelNowBody: 'The unpaid renewal is dropped and the subscription ends now: the account becomes read-only, and your data is kept for 90 days.',
    cancelConfirm: 'Cancel it',
    keep: 'Keep the subscription',
    change: 'Change plan',
    changeHint: 'The new plan and its price apply from the next renewal, on {{date}}.',
    switch: 'Switch at renewal',
    keepPlan: 'Keep this plan',
    from: 'From {{date}}',
    changeScheduled: '{{plan}} applies from {{date}}.',
    payments: 'Payments',
    paymentsEmpty: 'No payment yet.',
    paymentDate: 'Date',
    paymentFor: 'For',
    paymentPeriod: 'Period',
    paymentAmount: 'Amount',
    paymentReason: { setup_intent: 'First payment', cycle_billing: 'Renewal', other: 'Payment' },
    amountVat: '{{amount}} ({{net}} excl. VAT)',
  },
  orgSettings: {
    general: 'General',
    name: 'Name',
    legalName: 'Legal name',
    sirenHint: 'Nine digits, for invoices.',
    vat: 'VAT number',
    timezone: 'Default time zone',
    timezoneHint: 'For new sites, reports and exports.',
    language: 'Default language',
    languageHint: 'For invitations and external contacts.',
    security: 'Security',
    require2fa: 'Two-step verification',
    require2faHint: 'Members who have not turned it on are asked to before they continue.',
    ownerOnly: 'Only an owner can change this.',
    require2faNone: 'Optional',
    require2faAdmins: 'Required for owners and administrators',
    require2faAll: 'Required for everyone',
    enable2faFirst: 'Turn on two-step verification on your own account first.',
    supportAccess: 'Allow our support team to view this account when you ask for help (every access is recorded).',
    usage: 'Plan usage — {{plan}}',
    quotaPeriod: 'Messages counted since {{date}}.',
    danger: 'Erase the organisation',
    erasureScheduled: 'Erasure scheduled for {{date}}.',
    cancelErasure: 'Cancel the erasure',
    erasureHint: 'All sites, equipment, readings and records are erased after 7 days. Download your exports first.',
    erase: 'Erase the organisation',
    eraseConfirm: 'This cannot be undone once the 7 days are over. Type “{{name}}” to confirm.',
    typeName: 'Organisation name',
  },
  usage: {
    sites: 'Sites',
    assets: 'Equipment',
    devices: 'Devices',
    probes: 'Probes',
    users: 'Users',
    sms: 'SMS this month',
    whatsapp: 'WhatsApp this month',
  },
  members: {
    invite: 'Invite someone',
    member: 'Member',
    role: 'Role',
    twoFactor: 'Two-step',
    since: 'Since',
    leave: 'Leave',
    leaveConfirm: 'Leave this organisation? You will need a new invitation to come back.',
    remove: 'Remove',
    removeConfirm: 'Remove {{name}} from the organisation?',
    lastOwner: 'An organisation needs at least one owner: name another owner first.',
    limit: 'Your plan’s user limit is reached. Change plan to add more.',
    already: 'This person is already a member.',
    pending: 'Pending invitations',
    expires: 'Expires',
    revoke: 'Revoke',
    send: 'Send the invitation',
  },
  structure: {
    subtitle: 'Each site has its own time zone: its daily records and reports follow it.',
    addSite: 'Add a site',
    editSite: 'Edit the site',
    firstSite: 'Create your first site, then add its equipment.',
    siteName: 'Site name',
    address: 'Address',
    timezoneHint: 'Where the site is.',
    archive: 'Archive',
    archiveSiteConfirm: 'Archive the site “{{name}}”? Its records are kept.',
    siteInUse: 'Archive or move this site’s equipment first.',
    addAsset: 'Add equipment',
    editAsset: 'Edit the equipment',
    assetName: 'Name',
    type: 'Type',
    site: 'Site',
    notes: 'Notes',
    applyTemplate: 'Add the recommended alert rules',
    archiveAssetConfirm: 'Archive “{{name}}”? Its probes are unassigned and its records are kept.',
    limit: 'Your plan’s limit is reached. Change plan to add more.',
    tariff: 'Tariff',
    ambientProbe: 'Room temperature probe',
    ambientProbeHint: 'Optional: a probe measuring the room around the cold units, to compare their consumption at equal temperature.',
    noAmbientProbe: 'None',
    volume: 'Inside volume (litres)',
    volumeHint: 'Optional: to compare consumption and cost per litre.',
    volumeInvalid: 'Enter a volume in litres, or leave it empty.',
    rangeInvalid: 'The maximum must be above the minimum.',
    location: 'Location',
    locationHint: 'For the map of your sites: click on the map, drag the pin, or type the coordinates.',
    latitude: 'Latitude',
    longitude: 'Longitude',
    clearLocation: 'Remove the location',
    locationInvalid: 'Enter a latitude (−90 to 90) and a longitude (−180 to 180), in decimal degrees, or leave both empty.',
    pin: 'The site’s location',
    onMap: 'On the map',
    notOnMap: 'No location',
  },
  templates: {
    fridge: 'Warning above 5 °C for 30 min, critical above 8 °C for 15 min, warning below 0 °C for 1 h, door open 5 min.',
    freezer: 'Warning above −15 °C for 45 min, critical above −12 °C for 30 min, door open 3 min.',
    cold_room: 'Warning above 5 °C for 30 min, critical above 8 °C for 15 min, warning below 0 °C for 1 h, door open 10 min.',
    blast_freezer: 'Critical above −10 °C for 90 min, door open 10 min.',
    truck: 'Warning above 5 °C for 30 min, critical above 8 °C for 15 min, warning below 0 °C for 1 h, door open 15 min.',
    other: 'From the range you enter: warning above the maximum for 30 min, critical 3 °C above it for 15 min, warning below the minimum for 1 h. Without a range, no alert: add them yourself.',
  },
  rules: {
    subtitle: 'When an alarm is raised, and for whom. Every change is kept for the records.',
    appliesTo: 'Applies to',
    assets: 'Equipment',
    devices: 'Devices',
    noScope: 'Add equipment or a device first.',
    list: 'Rules',
    add: 'Add a rule',
    edit: 'Edit the rule',
    none: 'No rule: no alarm will be raised.',
    rule: 'Rule',
    condition: 'Condition',
    state: 'State',
    oneProbe: 'one probe only',
    enabled: 'On',
    disabled: 'Off',
    enable: 'Turn on',
    disable: 'Turn off',
    history: 'History',
    archiveConfirm: 'Archive this rule? Its history is kept.',
    stale: 'Someone else has just changed this rule. The page has been refreshed: check and try again.',
    type: 'Type',
    severity: 'Severity',
    severityHint: 'Critical alarms can break through quiet hours.',
    threshold: 'Threshold',
    holdMin: 'For at least (minutes)',
    holdHint: 'Short spikes (door opened, delivery) do not raise an alarm.',
    hysteresis: 'Back to normal margin',
    hysteresisHint: '°C on the safe side of the threshold before it counts as back to normal.',
    clearDelayMin: 'Back to normal for (minutes)',
    delta: 'Change of',
    windowMin: 'Within (minutes)',
    direction: 'Direction',
    dir: {
      rising: 'Rising',
      falling: 'Falling',
      either: 'Either',
    },
    afterMin: 'After (minutes)',
    doorHint: 'Door open longer than this.',
    offlineHint: 'No news from the device for longer than this.',
    batteryV: 'Below (volts)',
    policy: 'Escalation',
    policyHint: 'Who is alerted, and in what order.',
    policyDefault: 'The organisation’s default',
    name: 'Name',
    nameHint: 'Optional, e.g. “Fish counter”.',
    versionHint: 'Saving creates a new version: alarms keep the version they were raised under.',
    historyTitle: 'Rule history',
    version: 'Version',
    inForce: 'In force',
    changedBy: 'Changed by',
    now: 'now',
    summaryHigh: 'above {{value}} for {{hold}}',
    summaryLow: 'below {{value}} for {{hold}}',
    summaryRate: 'changes by {{delta}} within {{window}}',
    summaryAfter: 'after {{after}}',
    summaryBattery: 'below {{value}} V for {{hold}}',
    summaryHold: 'for {{hold}}',
    summaryCooling: 'from {{start}} to {{target}} within {{minutes}} min (HACCP settings)',
    meters: 'Power meters',
    energyHint: 'Power meter rules watch the equipment’s electricity: a unit that stops drawing power (breaker, unplugged), a compressor that never stops or starts too often, a meter that stops reporting.',
    minW: 'Below (W)',
    minWHint: 'A running fridge draws more than this, even between cycles.',
    noConsumptionHint: 'For longer than this: the equipment has lost power.',
    maxOnMin: 'Running for more than (minutes)',
    maxOnHint: 'A compressor that never stops: a leak, icing or a door left open.',
    maxStarts: 'More than (starts per hour)',
    maxStartsHint: 'Short cycles wear the compressor out.',
    meterOfflineHint: 'No data from the meter for longer than this.',
    summaryNoConsumption: 'below {{w}} W for {{hold}}',
    summaryContinuous: 'running for more than {{hold}}',
    summaryShortCycling_one: 'more than {{count}} start per hour',
    summaryShortCycling_other: 'more than {{count}} starts per hour',
  },
  mutes: {
    title: 'Muted periods',
    add: 'Mute alerts',
    hint: 'During a defrost, a cleaning or a delivery. Readings are still recorded, and excursions still show in the record.',
    kind: 'When',
    adhoc: 'From now',
    scheduled: 'Every week',
    mode: 'Mode',
    suppress: 'No alarm',
    relax: 'Raise the threshold',
    modeHint: {
      suppress: 'No alarm is raised during the period.',
      relax: 'Alarms are raised above a higher threshold only.',
    },
    relaxThreshold: 'Threshold during the period',
    hours: 'For (hours)',
    maxAdmin: 'Up to 72 hours.',
    maxOperator: 'Up to 4 hours.',
    days: 'Days',
    start: 'Starting at',
    startHint: 'Local time of the site.',
    durationMin: 'For (minutes)',
    reason: 'Reason',
    none: 'No muted period.',
    until: 'until {{date}}',
    every: 'every {{days}} at {{start}} for {{duration}}',
    cancel: 'End',
  },
  weekday: {
    '1': 'Mon',
    '2': 'Tue',
    '3': 'Wed',
    '4': 'Thu',
    '5': 'Fri',
    '6': 'Sat',
    '7': 'Sun',
  },
  escalation: {
    subtitle: 'Who is alerted, and when the alert goes to the next person if nobody acknowledges it.',
    addPolicy: 'Add a policy',
    editPolicy: 'Edit the policy',
    default: 'Default',
    deleteConfirm: 'Delete this policy? Rules that use it switch to the default.',
    inUse: 'Rules still use this policy.',
    contactInUse: 'Policies still alert this contact.',
    noLevels: 'Nobody is alerted by this policy.',
    levelN: 'Level {{n}}',
    immediately: 'immediately',
    after: 'after {{delay}}',
    reminders: 'Reminder every {{every}} until the alarm is acknowledged.',
    contacts: 'External contacts',
    addContact: 'Add a contact',
    editContact: 'Edit the contact',
    contactsHint: 'People without an account: a refrigeration engineer, a security company.',
    noContacts: 'No external contact.',
    contactName: 'Name',
    reach: 'Reached by',
    external: 'external',
    policyName: 'Name',
    wholeOrg: 'The whole organisation',
    reminderMin: 'Remind every (minutes)',
    reminderHint: 'Empty: no reminder.',
    delayMin: 'After (minutes)',
    delayHint: 'After the alarm, if nobody has acknowledged it.',
    recipient: 'Recipient',
    channel: 'Channel',
    theirChannels: 'Their preferred channels',
    addRecipient: 'Add a recipient',
    removeLevel: 'Remove the level',
    addLevel: 'Add a level',
    channels: 'Channels',
  },
  notifications: {
    subtitle: 'How you are alerted for this organisation. The first channel that works is used.',
    up: 'Move up',
    down: 'Move down',
    notSetUp: 'Set it up in your profile first.',
    notInPlan: 'Not included in the plan.',
    quiet: 'Quiet hours',
    quietStart: 'From',
    quietEnd: 'To',
    criticalOverrides: 'Critical alarms break through quiet hours',
    onClear: 'Tell me when a temperature is back to normal',
    hint: 'Emails are always sent as well, for the record.',
  },
  exports: {
    subtitle: 'The readings as CSV files, for your records or an inspection. Files are kept for 7 days.',
    new: 'New export',
    last7: 'Last 7 days',
    last30: 'Last 30 days',
    lastMonth: 'Last month',
    from: 'From',
    to: 'To',
    inclusive: 'Included.',
    tier: 'Detail',
    maxDays: 'Up to {{days}} days per export.',
    tiers: {
      raw: 'Every reading',
      '5m': '5-minute averages',
      '1h': 'Hourly averages',
      local_day: 'Daily minimum, maximum and average',
    },
    assets: 'Equipment',
    assetsAll: 'All equipment.',
    assetsSome: 'Equipment selected: {{n}}.',
    clear: 'All equipment',
    timezone: 'Days and times in {{tz}}.',
    create: 'Prepare the file',
    toBeforeFrom: 'The end is before the start.',
    tooLong: 'Too long for this detail: {{days}} days at most.',
    tooMany: 'Three exports are already being prepared. Wait for one to finish.',
    recent: 'Recent exports',
    none: 'No export yet.',
    requested: 'Requested',
    period: 'Period',
    state: 'State',
    size: 'Size',
    download: 'Download',
    until: 'until {{date}}',
    energy: {
      title: 'Energy report',
      hint: 'A month of consumption and cost per piece of equipment, with its drift and the comparisons of the month: for the organisation, a site or one piece of equipment.',
      month: 'Month',
      scope: 'For',
      all: 'The whole organisation',
      sites: 'Sites',
      format: 'Format',
      pdfNotInPlan: 'PDF with a higher plan.',
      queued: 'The report is being prepared: it will show below.',
    },
    kind: {
      csv_readings: 'CSV readings',
      haccp_pdf: 'HACCP report',
      haccp_csv: 'HACCP records (CSV)',
      gdpr_export: 'Full export',
      maintenance_csv: 'Maintenance report (CSV)',
      maintenance_pdf: 'Maintenance report (PDF)',
      energy_csv: 'Energy report (CSV)',
      energy_pdf: 'Energy report (PDF)',
    },
    states: {
      queued: 'Queued',
      running: 'Preparing',
      succeeded: 'Ready',
      failed: 'Failed',
      expired: 'Expired',
    },
  },
  audit: {
    subtitle: 'Every change made in this organisation. The log is chained: it cannot be altered without it showing.',
    group: 'Kind of action',
    allActions: 'All actions',
    actorFilter: 'By',
    everyone: 'Everyone',
    at: 'When',
    who: 'Who',
    action: 'Action',
    details: 'Details',
    none: 'No entry.',
    formerMember: 'Former member',
    staffAs: 'Support team, as {{name}}',
    actor: {
      user: 'Member',
      staff: 'Support team',
      system: 'System',
      device: 'Device',
    },
    groups: {
      alarm: 'Alarms',
      alarm_rule: 'Alert rules',
      annotation: 'Notes',
      asset: 'Equipment',
      contact: 'Contacts',
      device: 'Devices',
      escalation_policy: 'Escalation',
      export: 'Exports',
      invitation: 'Invitations',
      membership: 'Team',
      mute_window: 'Muted periods',
      organization: 'Organisation',
      probe: 'Probes',
      site: 'Sites',
      support: 'Support team',
    },
  },
  profile: {
    title: 'My profile',
    details: 'Details',
    email: 'Email',
    verified: 'Confirmed',
    unverified: 'Not confirmed',
    name: 'Name',
    language: 'Language',
    languageHint: 'Of the dashboard and of the alerts you receive.',
    timezoneHint: 'For the times in your alerts.',
    password: 'Password',
    newPassword: 'New password',
    confirmPassword: 'Confirm the password',
    passwordMismatch: 'The two passwords differ.',
    passwordOthers: 'Your other sessions will be signed out.',
    changePassword: 'Change the password',
    passwordChanged: 'Password changed. Your other sessions have been signed out.',
    twoFactor: 'Two-step verification',
    on: 'On',
    off: 'Off',
    twoFactorIntro: 'At sign-in, a code from an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…) is asked for after the password.',
    enable2fa: 'Turn on two-step verification',
    qrAlt: 'QR code to scan with the authenticator app',
    step1: 'Scan this QR code with your authenticator app.',
    step2: 'Enter the 6-digit code it shows.',
    manualKey: 'Cannot scan? Enter this key:',
    code: 'Code',
    activate: 'Turn on',
    wrongCode: 'Wrong code. Check your phone’s clock and try again.',
    recoveryTitle: 'Save your recovery codes',
    recoveryHint: 'Each code works once, if you lose your phone. Keep them somewhere safe: they are not shown again.',
    copy: 'Copy',
    download: 'Download',
    savedCodes: 'I have saved them',
    regenerate: 'New recovery codes',
    regenerateConfirm: 'Replace your recovery codes? The current ones stop working.',
    disable2fa: 'Turn off',
    disable2faConfirm: 'Turn off two-step verification? Your account will be protected by the password only.',
    twoFactorOff: 'Two-step verification is off.',
    requiredByOrg: 'An organisation you belong to requires it: {{detail}}.',
    phone: 'Mobile phone',
    noPhone: 'No number yet: add one to receive alerts by SMS or WhatsApp.',
    addPhone: 'Mobile number',
    newPhone: 'New number',
    phoneHint: 'International format, e.g. +33 6 12 34 56 78.',
    sendCode: 'Send a code',
    smsCode: 'Code sent by SMS to {{phone}}',
    verify: 'Confirm',
    phoneVerified: 'Number confirmed.',
    phoneConfirmed: 'Confirmed',
    phoneUnconfirmed: 'Not confirmed',
    unreachable: 'No SMS can be sent to this number.',
    channels: 'Messaging',
    channelsIntro: 'Where you can receive alerts. Choose the order per organisation in “My notifications”.',
    connected: 'Connected',
    phoneFirst: 'Confirm a mobile number first.',
    whatsappHint: 'Alerts on WhatsApp, to your confirmed number.',
    optIn: 'Receive alerts',
    disconnect: 'Disconnect',
    telegramHint: 'Alerts from our Telegram bot.',
    telegramPending: 'Open the bot and press Start (link valid until {{time}}).',
    openTelegram: 'Open Telegram',
    telegramUnavailable: 'Telegram is not available on this service.',
    connect: 'Connect',
    sessions: 'Where you are signed in',
    device: 'Device',
    lastSeen: 'Last activity',
    signedIn: 'Signed in',
    thisDevice: 'This device',
    signOutSession: 'Sign out',
  },
  maintenance: {
    tab: 'Maintenance',
    tabTemperatures: 'Temperatures',
    notInPlan: 'Maintenance tracking is not in your plan',
    notInPlanHint: 'Change plan in Settings → Subscription to log interventions and follow costs.',
    disclaimer: 'Book values and depreciation are computed for operational decision support. They are not official accounting figures.',
    thresholdReached: 'Threshold reached',
    thresholdNear: 'Near the threshold',
    thresholdOk: 'Below the threshold',
    thresholdReachedTitle: 'Maintenance now costs more than the equipment is worth',
    thresholdReachedBody: 'Maintenance cost {{basis}} is {{cost}}, for a book value of {{value}}. Consider replacing it.',
    basis: { rolling_12m: 'over the last 12 months', cumulative: 'since purchase' },
    basisLong: { rolling_12m: 'Last 12 months (recommended)', cumulative: 'Everything since purchase' },
    bookValue: 'Book value',
    age: '{{years}} years old',
    noProfileShort: 'No financial profile',
    cumulative: 'Cumulative cost',
    perYear: '{{value}} per year of age',
    rolling: '12-month cost',
    interventionsLast12_one: '{{count}} intervention in 12 months',
    interventionsLast12_other: '{{count}} interventions in 12 months',
    mtbf: 'Mean time between failures',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    trend: { rising: 'Interventions rising', stable: 'Interventions stable', falling: 'Interventions falling' },
    threshold: 'Replacement threshold',
    needsProfile: 'Add the financial profile to compare costs with book value.',
    thresholdLine: 'Cost {{basis}} against {{margin}} % of book value',
    projection: 'Projected crossing',
    projectionNotInPlan: 'Projections come with the Pro and Multi-site plans.',
    crossingOn: 'Around {{date}}',
    range: 'Between {{from}} and {{to}} (95 % range)',
    rangeOpen: '{{from}} at the earliest',
    status: {
      exceeded: 'The threshold is already reached.',
      not_within_horizon: 'Not expected within 15 years at the current trend.',
      insufficient_data: 'Not enough history yet: three interventions or twelve months are needed.',
    },
    statusShort: { exceeded: 'Reached', projected: 'Projected', not_within_horizon: 'Beyond 15 years', insufficient_data: 'Not enough history' },
    model: { linear: 'Linear trend', exponential: 'Accelerating trend (exponential)' },
    points_one: '{{count}} point',
    points_other: '{{count}} points',
    thresholdSeries: 'Threshold',
    date: 'Date',
    crossingShort: 'Crossing ~ {{date}}',
    crossedShort: 'Threshold crossed {{date}}',
    chartTitle: 'Book value and maintenance cost',
    chartHint: 'Dashed: projection at the current trend. Shaded: the range of the projected crossing.',
    profile: 'Financial profile',
    addProfile: 'Add',
    noProfile: 'No financial profile yet: add the purchase and depreciation to follow book value.',
    purchaseDate: 'Purchase date',
    purchaseValue: 'Purchase value',
    purchaseValueHt: 'Purchase value excl. VAT',
    method: 'Depreciation',
    methods: { straight_line: 'Straight-line', declining_balance: 'Declining balance' },
    usefulLife: 'Useful life',
    usefulLifeYears: 'Useful life (years)',
    years_one: '{{count}} year',
    years_other: '{{count}} years',
    residual: 'Residual value',
    decliningRate: 'Yearly rate (%)',
    decliningDefault: 'Default: double the straight-line rate',
    warrantyEnd: 'Warranty until',
    supplier: 'Supplier',
    brand: 'Brand',
    model_: 'Model',
    brandModel: 'Brand and model',
    serial: 'Serial number',
    schedules: 'Preventive maintenance',
    addSchedule: 'Schedule',
    scheduleName: 'What',
    scheduleNamePlaceholder: 'Compressor service, condenser cleaning…',
    everyMonths: 'Every (months)',
    firstDue: 'First due on',
    remindDays: 'Remind (days before)',
    noSchedules: 'No preventive maintenance scheduled.',
    every_one: 'Every month',
    every_other: 'Every {{count}} months',
    due: 'due {{date}}',
    lastDone: 'last done {{date}}',
    scheduleStatus: { ok: 'On schedule', due_soon: 'Due soon', overdue: 'Overdue' },
    archive: 'Remove',
    log: 'Maintenance log',
    logHint: 'Entries are never changed: a correction, a note or a cancellation is added to the entry and recorded in the audit log.',
    addIntervention: 'Log an intervention',
    invalidAmount: 'Check the amounts: numbers in euros, e.g. 120.50.',
    type: 'Type',
    types: { preventive: 'Preventive', corrective: 'Corrective', emergency: 'Emergency' },
    provider: 'Provider',
    description: 'What was done',
    parts: 'Parts (excl. VAT)',
    labour: 'Labour (excl. VAT)',
    travel: 'Travel (excl. VAT)',
    downtime: 'Downtime (minutes)',
    linkedAlarm: 'Temperature alarm',
    linkedAlarmHint: 'If the intervention answered an alarm (e.g. a compressor failure), it is recorded in the alarm\'s history too.',
    noAlarm: 'None',
    fulfils: 'Fulfils the schedule',
    invoice: 'Invoice (PDF or photo)',
    photo: 'Photo',
    total: 'Total',
    noInterventions: 'No intervention logged yet.',
    corrected: 'Corrected',
    voided: 'Cancelled',
    openAlarm: 'Linked alarm',
    costLine: 'parts {{parts}}, labour {{labour}}, travel {{travel}}',
    downtimeLine: '{{minutes}} min downtime',
    loggedBy: 'logged by {{name}}',
    attachment: { invoice: 'Invoice', photo: 'Photo' },
    annotationKind: { note: 'Note', correction: 'Correction', void: 'Cancellation' },
    field: { performed_on: 'date', type: 'type', description: 'description', provider: 'provider', parts_cents: 'parts',
      labour_cents: 'labour', travel_cents: 'travel', downtime_minutes: 'downtime' },
    note: 'Note',
    correct: 'Correct',
    void: 'Cancel',
    reason: { note: 'Note', correction: 'Why the correction', void: 'Why cancel it' },
    confirm: { note: 'Add the note', correction: 'Record the correction', void: 'Cancel the entry' },
    appendOnly: 'The original entry stays in the log.',
    exportCsv: 'Export CSV',
    exportPdf: 'Export PDF',
    exportQueued: 'The report is being prepared.',
    openExports: 'Open the exports',
    fleetTitle: 'Maintenance',
    fleetSubtitle: 'Every asset by replacement urgency: maintenance cost against book value.',
    fleetNotInPlan: 'The fleet ranking comes with the Pro and Multi-site plans',
    fleetNotInPlanHint: 'Log interventions on each asset\'s Maintenance tab; projections and the ranking come with a higher plan.',
    seePlans: 'See the plans',
    ranking: 'Replacement urgency',
    site: 'Site',
    allSites: 'All sites',
    sortBy: 'Sort by',
    sortUrgency: 'Urgency',
    sortSite: 'Site',
    asset: 'Equipment',
    urgency: 'Urgency',
    basisCost: 'Maintenance cost',
    crossing: 'Projected crossing',
    overdue: 'Overdue',
    noAssets: 'No equipment.',
    urgencyHint: 'Urgency is 100 once the threshold is reached; otherwise it grows as the cost nears the threshold and as the projected crossing nears. Updated after every change and every night.',
    settings: 'Replacement threshold and advisories',
    costBasis: 'Compare',
    costBasisHint: 'The maintenance cost compared with book value.',
    margin: 'Alert at (% of book value)',
    marginHint: '100: when the cost reaches the book value; 80: earlier.',
    digest: 'Email digest',
    digestHint: 'To Owners and Admins: assets nearing replacement, overdue maintenance, spend by site.',
    digestNotInPlan: 'With the Pro and Multi-site plans.',
    digestFrequency: { off: 'Off', monthly: 'Monthly', quarterly: 'Quarterly' },
    advisoryHint: 'Advisories go to Owners and Admins through their alert channels, on weekdays from 9:00 to 18:00, never in their quiet hours.',
  },
  meters: {
    title: 'Meter “{{meter}}”',
    settings: 'Meter settings',
    model: 'Model',
    phases: 'Phases',
    lastData: 'Last data',
    noData: 'No data yet',
    connection: 'Connection',
    direct: 'Directly (no gateway)',
    viaGateway: 'Through the gateway',
    host: 'Address on the local network',
    hostHint: 'Only if the gateway cannot find the meter by itself (mDNS). An IP address or a host name.',
    hostSeen: 'Found by the gateway at {{host}}. Leave empty to rely on discovery.',
    thresholds: 'Compressor thresholds',
    learned: 'Learned from the meter’s data ({{date}}). Type values to set them by hand.',
    manual: 'Set by hand ({{date}}).',
    learning: 'Not learned yet: the meter learns them from its first hours of data. You can also set them by hand.',
    onW: 'Running above (W)',
    onWHint: 'The compressor counts as running above this power.',
    offW: 'Stopped below (W)',
    offWHint: 'Optional; at most the running threshold.',
    defrostW: 'Defrost above (W)',
    defrostWHint: 'Optional: the defrost heater draws more than the compressor.',
    relearn: 'Forget and learn again',
    relearnHint: 'The meter learns them again from its next hours of data.',
    shelly: {
      add: 'Add a Shelly without a gateway',
      title: 'Add a Shelly meter without a gateway',
      intro: 'For a site without a gateway: the Shelly connects to the platform directly, through its outbound WebSocket. The platform only reads it; it never switches it.',
      quota: 'Meters in use: {{used}} of {{max}} in your plan.',
      mac: 'MAC address',
      macHint: 'In the Shelly app, or on its label (12 hexadecimal characters).',
      modelHint: 'e.g. S3PM-001PCEU16 (Plus Plug S), SPEM-003CEBEU (Pro 3EM).',
      single: 'Single-phase',
      three: 'Three-phase',
      channels: 'Channels',
      channelsHint: 'Circuits measured by this Shelly (1 for a plug).',
      submit: 'Add the meter',
      already: 'This Shelly is already enrolled.',
      limit: 'Your plan’s meter limit is reached. Change plan to add more.',
      once: 'Copy this address now',
      onceHint: 'It is shown only once: it holds the meter’s key. If it is lost, make a new one from the device’s page.',
      address: 'Connection address',
      copy: 'Copy',
      copied: 'Copied',
      step1: 'Open the Shelly’s web page (its IP address) or the Shelly app, then Settings → Outbound WebSocket.',
      step2: 'Turn it on and paste the address as the server.',
      step3: 'Leave TLS verification on (“Default TLS”), then save: the Shelly reconnects on its own.',
      step4: 'Within a minute the device shows as online here. Then pair its meter with the equipment it powers.',
      readOnly: 'Recommended: set the relay to switch on at power-up and lock its button, so that nobody switches the equipment off by mistake.',
      openDevice: 'Open the device',
      done: 'Done',
      newAddress: 'New address',
      newAddressHint: 'A new connection address is made, and the current one stops working at once: the Shelly disconnects until you enter the new address in it.',
      waiting: 'Enter its connection address in the Shelly (Settings → Outbound WebSocket). If you no longer have it, make a new one.',
    },
  },
  energy: {
    tab: 'Energy',
    disclaimer: 'Estimates from the meters and the site’s tariff, for comparing and deciding: they are not a bill.',
    notInPlan: 'Energy monitoring is not in your plan',
    notInPlanHint: 'Measure what each fridge and cold room consumes and costs, with power meters. Available with a higher plan.',
    insightsNotInPlan: 'Energy insights are not in your plan',
    insightsNotInPlanHint: 'Normal consumption learned per equipment, drift alerts with likely causes, efficiency, before/after comparisons, fleet ranking and replacement payback: with a higher plan.',
    noMeter: 'No power meter on this equipment',
    noMeterHint: 'Pair a power meter (a Shelly read by the gateway, or connected directly) with this equipment on the Devices page.',
    toDevices: 'Go to devices',
    today: 'Today',
    last7: 'Last 7 days',
    last30: 'Last 30 days',
    monthToDate: 'This month',
    projectedAnnual: 'Projected per year',
    needs7Days: 'After 7 full days',
    partial: '(data {{pct}} %)',
    chart: {
      title: 'Temperature and power',
      time: 'Time',
      power: 'Power',
      defrost: 'Defrost',
      door: 'Door open',
      alarm: 'Alarm',
      maintenance: 'Intervention',
      tier: {
        minute: 'Power per minute.',
        hour: 'Average power per hour.',
        day: 'Average power per day.',
      },
      statesHint: 'Defrosts show over 8 days at most.',
    },
    cycles: {
      title: 'Compressor cycles',
      h24: '24 h',
      d7: '7 days',
      duty: 'Running time',
      perHour: 'Starts per hour',
      avgOn: 'Average run',
      avgOff: 'Average stop',
      longest: 'Longest run',
      defrosts: 'Defrosts',
      defrostShare: 'Defrost energy',
      runningW: 'Power while running',
      none: 'No cycle yet: the meter learns the compressor’s thresholds from its first hours of data.',
      hint: 'A compressor that runs longer, starts more often or draws more power than usual is a sign to watch.',
    },
    drift: {
      title: 'Normal consumption and drift',
      noBaseline: 'Normal consumption is learned from the first weeks of data.',
      learning: 'Learning normal consumption: {{days}} of {{needed}} days',
      learningHint: 'From {{date}}; days with gaps are left out.',
      baseline: 'Normal: {{kwh}} kWh a day (learned from {{date}}).',
      temperatureModel: 'Adjusted for the room temperature.',
      status: {
        normal: 'Normal',
        drift: 'Consuming more than usual',
        insufficient_data: 'Not enough data this week',
        no_baseline: 'No reference yet',
      },
      statusShort: {
        normal: 'Normal',
        drift: 'Drift',
        insufficient_data: 'Not enough data',
        no_baseline: 'Learning',
      },
      detail: 'Last 7 days: {{actual}} kWh against {{expected}} kWh expected (alert above +{{threshold}} %).',
      conditioned: 'Compared at the same room temperature.',
      insufficientHint: 'Fewer than 5 complete days in the last 7.',
      causesTitle: 'Likely causes',
      because: 'Because:',
      causesHint: 'Hints from the meter’s data, to guide a check: only a technician can confirm them.',
      relearn: 'Learn again',
      relearnTitle: 'Learn normal consumption again?',
      relearnHint: 'After a repair, a replacement or a change of use. The current reference is dropped and a new one is learned over the next {{days}} days; there is no drift alert meanwhile.',
    },
    cause: {
      dirty_condenser: {
        title: 'Dirty condenser or blocked airflow',
        text: 'The compressor draws more power than usual: it works harder to reject heat. Clean the condenser coil and check that nothing blocks the air around the unit.',
      },
      door_seal: {
        title: 'Door seal or door left ajar',
        text: 'The compressor runs longer without more door openings: warm air gets in. Check the seals and that the door closes fully.',
      },
      low_refrigerant: {
        title: 'Refrigerant leak or failing compressor',
        text: 'The compressor runs almost all the time and draws less power: it moves less heat. Have a refrigeration technician check it.',
      },
      icing: {
        title: 'Icing on the evaporator',
        text: 'Defrosts use a larger share of the energy: ice builds up. Check the defrost settings, the drain and the door seals.',
      },
      usage: {
        title: 'More use',
        text: 'The door is opened more often than usual: more warm air and warm products to cool. Not a fault.',
      },
      ambient: {
        title: 'Warmer room',
        text: 'The room around the equipment is warmer than when normal consumption was learned. Check the ventilation around it.',
      },
    },
    evidence: {
      running_w_up: 'more power while running',
      running_w_down: 'less power while running',
      duty_up: 'longer running time',
      near_continuous: 'runs almost all the time',
      doors_unchanged: 'as many door openings',
      doors_up: 'more door openings',
      defrost_up: 'more defrost energy',
      ambient_up: 'warmer room',
    },
    efficiency: {
      title: 'Efficiency (30 days)',
      perKelvin: 'Energy per degree of lift',
      perKelvinHint: 'Wh a day per °C between the room and the inside; lower is better.',
      perDoor: 'Energy per door opening',
      perDoorHint: 'The extra energy each opening costs.',
      perM3: 'Energy per m³ a year',
      volume: 'For {{litres}} L inside.',
      noVolume: 'Enter the volume in the equipment’s settings.',
      perLitre: 'Cost per litre a year',
      perLitreHint: 'To compare equipment of different sizes.',
    },
    tco: {
      title: 'Cost of ownership',
      energy: 'Energy',
      maintenance: 'Maintenance',
      total: 'Total',
      since: 'Energy since {{date}}, when the meter started; all logged maintenance.',
      noData: 'No complete day of energy yet.',
    },
    periods: {
      title: 'Consumption per period',
      by: 'Per',
      period: 'Period',
      energy: 'Energy',
      cost: 'Cost',
      day: 'Day (30 days)',
      week: 'Week (6 months)',
      month: 'Month (12 months)',
      year: 'Year',
      total: 'Total: {{kwh}}, {{cost}}.',
    },
    compare: {
      title: 'Before and after an intervention',
      none: 'No logged intervention on this equipment: log one in the Maintenance tab to compare.',
      intervention: 'Intervention',
      window: 'Days compared',
      daysEach_one: '± {{count}} day',
      daysEach_other: '± {{count}} days',
      before: 'Before',
      after: 'After',
      kwhDay: 'Energy a day',
      costDay: 'Cost a day',
      ambient: 'Room temperature',
      days: 'Complete days',
      change: 'Change',
      annualSavings: 'Savings a year',
      fewDays: 'Few complete days: the comparison is uncertain.',
      hint: 'Only complete days count. At the same room temperature when normal consumption models it.',
    },
    replace: {
      title: 'Replacement',
      add: 'Compare a new unit',
      none: 'Enter a replacement model (from its energy label) to see what it would save and when it would pay for itself.',
      label: 'Model',
      labelPlaceholder: 'e.g. Brand X 600 L, class C',
      kwhYear: 'kWh a year',
      kwhYearHint: 'From the energy label.',
      price: 'Price (excl. VAT)',
      class: 'Energy class',
      classBadge: 'Class {{cls}}',
      year: 'year',
      needsData: 'The savings show after 7 complete days of consumption.',
      annualSavings: 'Savings a year',
      split: 'Energy {{energy}} + maintenance avoided {{maintenance}}',
      payback: 'Payback',
      years: '{{value}} years',
      never: 'Not paid back',
      detail: 'Now {{current}} kWh a year, the new unit {{candidate}} kWh a year, at {{rate}} cents per kWh on average. Maintenance avoided: the last 12 months’.',
      remove: 'Remove',
    },
    fleetTitle: 'Energy',
    fleetSubtitle: 'What each piece of equipment consumes and costs, and which ones drift.',
    ranking: 'Equipment',
    sort: {
      cost: 'Cost',
      efficiency: 'Least efficient',
      drift: 'Drift',
      site: 'Site',
    },
    fleet: {
      kwh30: 'Energy, 30 days',
      cost30: 'Cost, 30 days',
      assets: 'Metered equipment',
      last30: '30 days',
      annual: 'Per year',
      perLitre: 'Per litre a year',
      efficiency: 'Efficiency',
      drift: 'Drift',
      duty: 'Running, 7 days',
      coverage: 'Data',
      none: 'No metered equipment yet.',
      whDayK: '{{value}} Wh/day/°C',
      hint: 'Per year: projected from the complete days of the last 30. Efficiency: kWh a year per m³ inside, and Wh a day per °C of lift. Updated every night.',
      reports: 'Monthly energy reports (PDF or CSV) are made from the exports page.',
    },
    settings: {
      title: 'Energy settings',
      drift: 'Drift alert above (%)',
      driftHint: 'Consumption of the last 7 days above normal by this much.',
      days: 'Learning period (days)',
      daysHint: 'Days of data to learn normal consumption.',
      report: 'Monthly report',
      reportHint: 'Emailed to Owners and Admins at the start of each month.',
      on: 'On',
      off: 'Off',
      meters: 'Meters in use: {{used}} of {{max}} in your plan.',
    },
    tariff: {
      title: 'Electricity tariff — {{site}}',
      hint: 'Prices per kWh (excl. VAT) from the site’s contract, and its fixed monthly cost. Each change is a new version from its date, so past costs stay as they were.',
      none: 'No tariff: consumption shows without its cost.',
      from: 'From {{date}}',
      inForce: 'In force',
      past: 'Past',
      planned: 'Planned',
      copy: 'Copy as a new version',
      delete: 'Delete',
      deleteConfirm: 'Delete the version planned from {{date}}?',
      add: 'New version:',
      kind: {
        base: 'Base',
        hp_hc: 'Peak / off-peak',
        tempo: 'Tempo',
        custom: 'Custom',
      },
      kindLabel: 'Kind',
      label: 'Name',
      labelPlaceholder: 'e.g. Supplier X, 36 kVA',
      effectiveFrom: 'From',
      effectiveHint: 'A past date prices the consumption since then again.',
      futureOnly: 'A planned version can only be moved to a later date.',
      rates: 'Prices',
      centsUnit: 'cents {{currency}} per kWh',
      rateName: 'Rate name',
      rateNames: {
        BASE: 'Base',
        HP: 'Peak (HP)',
        HC: 'Off-peak (HC)',
        HP_BLEU: 'Blue, peak',
        HC_BLEU: 'Blue, off-peak',
        HP_BLANC: 'White, peak',
        HC_BLANC: 'White, off-peak',
        HP_ROUGE: 'Red, peak',
        HC_ROUGE: 'Red, off-peak',
      },
      addRate: 'Add a rate',
      removeRate: 'Remove the rate',
      offpeakFrom: 'Off-peak from',
      offpeakTo: 'Off-peak until',
      offpeakHint: 'The hours in your contract; they may run past midnight.',
      dayStart: 'Tariff day starts at',
      dayStartHint: 'Tempo: 06:00.',
      tempoHint: 'Each day’s colour comes from the day-type calendar, shown under the versions once saved; a day without a colour is priced at the blue peak rate.',
      rules: 'Rules',
      rulesHint: 'The first rule that matches a time sets its price; otherwise the default rate. Empty months, days or hours mean all.',
      ruleRate: 'Rate',
      ruleFrom: 'From',
      ruleTo: 'To',
      ruleDayTypes: 'Day types',
      ruleMonths: 'Months',
      ruleWeekdays: 'Days',
      addRule: 'Add a rule',
      removeRule: 'Remove',
      defaultRate: 'Default rate',
      defaultRateHint: 'When no rule matches.',
      fixed: 'Fixed cost a month ({{currency}})',
      fixedHint: 'Subscription and other fixed charges, for the whole site.',
      fixedLine: 'Fixed cost {{amount}} a month, shared: {{allocation}}.',
      allocationLabel: 'Shared between equipment',
      allocation: {
        energy_share: 'By consumption',
        equal: 'Equally',
      },
      allocationHint: {
        energy_share: 'Each piece of equipment bears a share in proportion to what it consumes.',
        equal: 'Each metered piece of equipment bears the same share.',
      },
      currency: 'Currency',
      recomputeHint: 'Costs are recalculated overnight from the version’s date.',
      inForceError: 'A version in force or past is kept as it was: add a new version from the date of the change.',
      existsError: 'The site already has a version from that date.',
      errorRate: 'Enter a price for every rate.',
      errorFixed: 'The fixed cost is not a valid amount.',
      errorHours: 'Enter both times, or neither.',
    },
    days: {
      title: 'Day types (Tempo colours)',
      hint: 'Click a day to change its type ({{types}}), or paste a list from your supplier.',
      hintViewer: 'The type of each day, from the supplier.',
      prev: 'Previous month',
      next: 'Next month',
      save_one: 'Save {{count}} day',
      save_other: 'Save {{count}} days',
      saved_one: '{{count}} day saved.',
      saved_other: '{{count}} days saved.',
      paste: 'Paste a list',
      pasteLabel: 'One day per line',
      pasteHint: '“2026-01-15 rouge” or “15/01/2026 blanc”.',
      import: 'Import',
      nothing: 'No day could be read.',
      invalid: 'Lines not read: {{lines}}',
    },
    month: {
      '1': 'Jan',
      '2': 'Feb',
      '3': 'Mar',
      '4': 'Apr',
      '5': 'May',
      '6': 'Jun',
      '7': 'Jul',
      '8': 'Aug',
      '9': 'Sep',
      '10': 'Oct',
      '11': 'Nov',
      '12': 'Dec',
    },
  },
  haccpSync: {
    badge: {
      synced: 'Synced',
      pending_one: '{{count}} waiting',
      pending_other: '{{count}} waiting',
      sending: 'Sending…',
      offline: 'Offline',
      offlinePending_one: 'Offline · {{count}} waiting',
      offlinePending_other: 'Offline · {{count}} waiting',
      signIn_one: '{{count}} waiting · sign in',
      signIn_other: '{{count}} waiting · sign in',
      failed_one: '{{count}} to review',
      failed_other: '{{count}} to review',
    },
    tag: {
      pending: 'Waiting',
      failed: 'To review',
    },
    title: 'Records on this phone',
    offlineHint: 'No network: records are kept on this phone and sent as soon as the network is back.',
    pendingHint: 'These records are waiting to be sent; they keep the time they were entered.',
    unreachableHint: 'The server cannot be reached for now: a new attempt is made automatically.',
    signInHint: 'Your session has ended: sign in again to send these records.',
    allSent: 'Everything entered on this phone has been sent.',
    policy: 'Records waiting here belong to you: if someone else signs in on this phone, they will neither see nor send them. Signing out keeps them, to be sent at your next sign-in (within 7 days of entry).',
    sendNow: 'Send now',
    see: 'See',
    retry: 'Try again',
    discard: 'Remove from this phone',
    discardConfirm: 'Remove this record from the phone? It was not saved: enter it again if needed.',
    failedTitle_one: '{{count}} record was refused',
    failedTitle_other: '{{count}} records were refused',
    failedHint: 'They were entered offline and the server did not accept them. Their details are kept: check them, enter them again if needed, then remove them.',
    queued: 'Kept on this phone: it will be sent as soon as the network is back.',
    full: 'This phone has no more room for records waiting to be sent: send the waiting ones first, or remove the photo.',
    needsNetwork: 'This needs the network.',
    waitingTick: 'Already ticked on this phone: it will be sent as soon as the network is back.',
    tickedAt: 'Ticked at {{time}} · waiting to be sent',
    enteredAt: 'Entered on {{time}}',
    withPhoto: 'with a photo',
    task: 'Task',
    place: 'Where',
    signOutConfirm_one: '{{count}} HACCP record has not been sent yet. It stays on this phone and will be sent at your next sign-in. Sign out?',
    signOutConfirm_other: '{{count}} HACCP records have not been sent yet. They stay on this phone and will be sent at your next sign-in. Sign out?',
    kind: {
      cooling: 'Cooling',
      reception: 'Goods reception',
      cleaning: 'Cleaning',
    },
    error: {
      record_conflict: 'Another record was already saved under the same identifier.',
      occurrence_done: 'This task had already been ticked by someone else.',
      task_archived: 'This task was archived in the meantime.',
      photo_lost: 'The photo could not be kept on this phone: enter the record again.',
      validation_failed: 'The server refused the record (too old, or a value is no longer valid).',
      feature_not_in_plan: 'This register is no longer part of your plan.',
      forbidden: 'You are no longer allowed to enter this record.',
      other: 'The server refused the record.',
    },
  },
  cleaning: {
    today: 'Today',
    history: 'History',
    site: 'Site',
    summary: '{{done}} done · {{todo}} to do · {{overdue}} overdue',
    none: 'Nothing to clean today on this site.',
    noneHint: 'Admins set up the cleaning plan in Settings › HACCP.',
    noTasks: 'No cleaning task yet',
    noTasksHint: 'Add the cleaning plan\'s tasks in Settings › HACCP: equipment or zone, what to do, how often.',
    general: 'General',
    dueAt: 'by {{time}}',
    dueEndOfDay: 'by the end of the day',
    afterService: 'after {{service}}',
    status: {
      done: 'Done',
      due: 'To do',
      overdue: 'Overdue',
    },
    doneBy: '{{name}} at {{time}}',
    late: 'late',
    tick: 'Tick',
    tickTitle: 'Cleaning done',
    photo: 'Photo (optional)',
    comment: 'Comment (optional)',
    submit: 'Mark as done',
    saved: 'Ticked.',
    retry: 'Not sent (no connection?). What you entered is kept: try again.',
    detail: 'Cleaning done',
    method: 'Method or product',
    field: {
      comment: 'Comment',
    },
    readOnly: 'Ticking needs the Operator role or above.',
    period: 'Period',
    days_one: 'Last {{count}} day',
    days_other: 'Last {{count}} days',
    noHistory: 'Nothing ticked over this period.',
    occurrence: 'For {{date}}',
    version: 'version {{version}}',
    voided: 'Voided',
    open: 'Details',
    tile: 'Cleaning: {{done}} done / {{total}} due today',
    tileOverdue_one: '{{count}} overdue',
    tileOverdue_other: '{{count}} overdue',
    settings: {
      hint: 'Per site: the services (their end times start the "after service" tasks), then the tasks. Changing a task never changes what was ticked against it.',
      services: 'Services',
      servicesHint: 'The "after service" tasks are due at the end of each service (local time, the same day). Defaults: lunch until 15:00, dinner until 23:30.',
      defaults: 'Default services: check them, then save.',
      serviceLabel: 'Service',
      serviceEnd: 'Ends at',
      addService: 'Add a service',
      remove: 'Remove',
      tasks: 'Tasks',
      addTask: 'Add a task',
      noTasks: 'No task on this site yet.',
      editTask: 'Change the task',
      newTask: 'New cleaning task',
      archive: 'Archive',
      archiveConfirm: 'Archive this task? It stops appearing on the checklist; what was ticked stays in the record.',
      equipment: 'Equipment',
      noEquipment: '— none —',
      zone: 'Zone',
      zoneHint: 'E.g. Kitchen, Dishwashing area. An equipment, a zone, or both.',
      task: 'Task',
      method: 'Method or product',
      frequency: 'Frequency',
      freq: {
        after_service: 'After each service',
        daily: 'Every day',
        weekly: 'Every week',
        monthly: 'Every month',
      },
      weekdays: 'Days',
      weekdaysHint: 'None ticked: every day.',
      monthDay: 'Day of the month',
      monthDayHint: '31: the last day in shorter months.',
      dueTime: 'Due by',
      dueTimeHint: 'Empty: by the end of the day.',
      afterServices: 'After the services',
      afterServicesHint: 'None ticked: after every service.',
      role: 'Assigned to',
      roleHint: 'Reminders go to this role and the roles above.',
      remindAfter: 'Remind after (min overdue)',
      escalate: 'Then remind the Admins',
      escalateAfter: 'after (more min)',
      summaryDaily: 'every day',
      summaryWeekly: 'every {{days}}',
      summaryMonthly: 'on the {{day}} of each month',
      summaryAfter: 'after {{services}}',
      summaryAt: 'by {{time}}',
      version: 'v{{version}}',
    },
  },
  haccp: {
    title: 'HACCP records',
    subtitle: 'Cooling, goods receptions and cleaning, recorded as they happen.',
    notInPlan: 'HACCP records are not in your plan.',
    notInPlanHint: 'Ask us to add them to your subscription.',
    tabs: {
      cooling: 'Cooling',
      reception: 'Receptions',
      cleaning: 'Cleaning',
    },
    export: {
      button: 'Export',
      title: 'Export the HACCP records',
      hint: 'A PDF report for an inspection, or one record as CSV. Files are kept 7 days, also in Settings › Exports.',
      site: 'Site',
      allSites: 'All sites',
      from: 'From',
      to: 'To',
      days: 'Days included, in each site\'s local time. PDF: at most {{pdf}} days; CSV: {{csv}} days.',
      toBeforeFrom: 'The end is before the start.',
      tooLong: 'Too long: at most {{days}} days.',
      pdf: 'PDF report',
      pdfHint: 'Temperatures, alarms and corrective actions, then the records of your plan; each page carries the report\'s SHA-256 fingerprint.',
      pdfNotInPlan: 'The PDF report needs a higher plan.',
      csv: 'CSV, one record at a time',
      csvModule: {
        cooling: 'Cooling (CSV)',
        reception: 'Receptions (CSV)',
        cleaning: 'Cleaning (CSV)',
      },
      queued: 'The file is being prepared: it will appear below.',
      recent: 'Recent HACCP exports',
      none: 'No HACCP export yet.',
      close: 'Close',
    },
    soon: 'Coming soon',
    soonHint: 'This record will be available here shortly.',
    history: {
      title: 'Corrections',
      none: 'No corrections: the record is as it was entered.',
      correction: 'Correction',
      void: 'Voided',
      by: '{{name}}, {{time}}',
      reason: 'Reason: {{reason}}',
      empty: '(empty)',
    },
    correct: {
      button: 'Correct',
      title: 'Correct this record',
      hint: 'The record itself never changes: your correction is kept beside it, with your name and the reason.',
      kind: 'What do you want to do?',
      kindCorrection: 'Correct some fields',
      kindVoid: 'Void the record (entered by mistake)',
      voidHint: 'A void record stays in the log, struck through.',
      reason: 'Reason',
      reasonHint: 'Required, at least 3 characters.',
      save: 'Save the correction',
      saveVoid: 'Void the record',
      nothing: 'Change at least one field.',
      voided: 'Void',
    },
    cooling: {
      start: 'Start a cooling',
      running: 'In progress',
      recent: 'Cycles',
      none: 'No cooling recorded yet.',
      noneHint: 'Start one when a hot load goes into a blast chiller.',
      noRunning: 'No cooling in progress.',
      noChiller: 'No blast chiller yet',
      noChillerHint: 'Add equipment of type “Blast chiller” with a probe, in Settings › Sites and equipment.',
      chiller: 'Blast chiller',
      probe: 'Probe',
      noProbe: 'This blast chiller has no probe.',
      product: 'Product',
      quantity: 'Quantity',
      quantityHint: 'E.g. 12 kg, 3 gastronorm trays.',
      photo: 'Photo (optional)',
      startedAt: 'Load went in at',
      startedAtHint: 'Leave as is if it is now.',
      submit: 'Start the cooling',
      started: 'Cooling started.',
      retry: 'Not sent (no connection?). Your entry is kept: try again.',
      remaining: '{{time}} left',
      overdue: '{{time}} past the deadline',
      target: 'target {{value}}',
      now: 'Now {{value}}',
      deadline: 'Deadline {{time}}',
      reachedAt: 'Target reached at {{time}} ({{value}})',
      lateAlarm: 'Not at target in time: an alarm was raised.',
      openAlarm: 'Open the alarm',
      auto: 'Started automatically',
      autoHint: 'Detected from a hot load: fill in the product with a correction.',
      unknownProduct: 'Product to fill in',
      role: {
        air: 'air probe',
        core: 'core probe',
      },
      status: {
        running: 'In progress',
        compliant: 'Compliant',
        non_compliant: 'Non-compliant',
        cancelled: 'Cancelled',
      },
      close: 'Close',
      closeConfirm: 'Close this cycle? The load has left the blast chiller.',
      cancel: 'Cancel the cycle',
      cancelReason: 'Why cancel this cycle?',
      detail: 'Cooling cycle',
      back: 'All cycles',
      rule: 'From {{start}} to {{target}} within {{minutes}} min',
      ruleHint: 'Rule in force when the cycle started.',
      events: 'Events',
      event: {
        started: 'Started',
        target_reached: 'Target reached',
        late: 'Deadline passed without the target',
        closed: 'Closed',
        cancelled: 'Cancelled',
      },
      curve: 'Temperature',
      noCurve: 'No readings yet.',
      targetLine: 'Target',
      filledAt: 'Entered at {{time}}',
      syncedAt: 'received at {{time}}',
      field: {
        product: 'Product',
        quantity: 'Quantity',
        probe_role: 'Probe role',
        note: 'Note',
      },
      allSites: 'All sites',
      allStatuses: 'All statuses',
      site: 'Site',
      statusFilter: 'Status',
    },
    reception: {
      new: 'New reception',
      today: 'Today',
      days7: 'Last 7 days',
      days30: 'Last 30 days',
      period: 'Period',
      onlyNc: 'Non-conformities only',
      none: 'No reception recorded for this period.',
      noneHint: 'Record each delivery as it arrives: supplier, temperature, packaging, decision.',
      noSite: 'Add a site first, in Settings › Sites and assets.',
      site: 'Site',
      allSites: 'All sites',
      supplier: 'Supplier',
      supplierSearch: 'Type to search…',
      supplierAdd: 'Add the supplier “{{name}}”',
      supplierAdded: 'Supplier added.',
      supplierPick: 'Choose a supplier in the list, or add it.',
      category: 'Product category',
      noLimit: 'no limit',
      product: 'Products (optional)',
      productHint: 'E.g. salmon, 2 boxes; batch number.',
      temperature: 'Temperature',
      typeIn: 'Type in',
      readProbe: 'Read a probe',
      temperatureC: 'Temperature (°C)',
      flipSign: 'Change the sign',
      temperatureInvalid: 'Enter a temperature, e.g. 3,5 or −18.',
      temperatureRequired: 'This category needs a temperature.',
      probe: 'Probe',
      noProbe: 'This site has no temperature probe with a recent reading.',
      probeReading: '{{value}} at {{time}}',
      probeStale: 'No recent reading',
      outOfLimit: 'Out of limit: {{limit}}',
      outOfLimitHint: 'Choose a decision and explain it in the comment.',
      limitMax: 'max {{value}}',
      limitMin: 'min {{value}}',
      packaging: 'Packaging intact and clean?',
      yes: 'Yes',
      no: 'No',
      deliveryNote: 'Photo of the delivery note',
      deliveryNoteHint: 'Optional. Opens the camera on a phone.',
      decision: 'Decision',
      comment: 'Comment',
      commentRequired: 'Required: explain the non-conformity, the reserve or the refusal.',
      commentOptional: 'Optional.',
      submit: 'Record the reception',
      saved: 'Reception recorded.',
      retry: 'Not sent (no connection?). Your entry is kept: try again.',
      back: 'All receptions',
      detail: 'Reception',
      receivedAt: 'Received at {{time}}',
      syncedAt: 'synced at {{time}}',
      by: 'by {{name}}',
      fromProbe: 'read on {{probe}} at {{time}}',
      typedIn: 'typed in',
      limitsAtTime: 'Limits when received: {{limits}}',
      noTemperature: 'No temperature',
      ncNotice: 'Non-conformity recorded.',
      decisions: {
        accepted: 'Accepted',
        accepted_with_reserve: 'Accepted with a reserve',
        refused: 'Refused',
      },
      status: {
        void: 'Void',
        refused: 'Refused',
        out_of_limit: 'Out of limit',
        packaging: 'Packaging',
        reserve: 'With a reserve',
        accepted: 'Accepted',
      },
      field: {
        supplier_id: 'Supplier',
        product: 'Products',
        packaging_ok: 'Packaging intact',
        decision: 'Decision',
        comment: 'Comment',
      },
      settings: {
        hint: 'Product categories and their temperature limits are set per site. A reception keeps the limits in force when it was recorded.',
        defaultsBadge: 'Default values to confirm with your PMS',
        defaultsHint: 'Check these limits against your PMS (food safety plan), adjust them if needed, then confirm them.',
        confirm: 'Confirm',
        confirmed: 'Confirmed on {{date}}',
        edit: 'Edit',
        archive: 'Archive',
        restore: 'Restore',
        archived: 'Archived',
        categories: 'Product categories',
        addCategory: 'Add a category',
        name: 'Name',
        minC: 'Minimum (°C)',
        maxC: 'Maximum (°C)',
        limitsHint: 'Leave empty for no limit. A temperature equal to the limit is within it.',
        suppliers: 'Suppliers',
        suppliersHint: 'Operators can also add a supplier from the reception form.',
        addSupplier: 'Add a supplier',
        contact: 'Contact',
        notes: 'Notes',
        noSuppliers: 'No supplier yet.',
        showArchived: 'Show archived',
      },
    },
    settings: {
      title: 'HACCP',
      retention: 'Keep HACCP records for (years)',
      retentionHint: 'Older records are deleted every night, with their photos. At least 1 year; 5 years by default.',
      cooling: 'Cooling',
      coolingHint: 'Per site. Each blast chiller of the site follows these values; a cycle keeps the values it started with.',
      defaults: 'Default values: check them against your PMS (plan de maîtrise sanitaire) and save them.',
      savedOn: 'Saved on {{date}}',
      startC: 'Start temperature (°C)',
      targetC: 'Target temperature (°C)',
      maxMinutes: 'Maximum time (min)',
      autoDetect: 'Start a cycle automatically on a hot load',
      autoHint: 'When a blast chiller probe rises this much this fast, a cycle starts by itself; the product is filled in afterwards.',
      jumpC: 'Rise of (°C)',
      jumpWindow: 'within (min)',
      reception: 'Receptions',
      receptionHint: 'Product categories and suppliers: coming soon.',
      cleaning: 'Cleaning',
      cleaningHint: 'The cleaning plan: each site\'s services and tasks.',
      noSites: 'No site yet.',
    },
  },
};
